/**
 * Background version-cache refresh — the network half of the update notifier.
 *
 * Runs ONLY in a detached child process that a prior foreground run spawned (see
 * `scheduleVersionRefresh`), never on the foreground path. It hits npm / GitHub, writes the result
 * to the on-disk cache, and exits — it NEVER prints. The foreground run shows the nudge from the
 * cache this populates ("show on next run": a refresh feeds the *next* invocation, so the current
 * command is never slowed by the network). All failures are swallowed — a stale nudge is fine, a
 * crashed background refresher is not.
 */

import { exec, spawn } from 'node:child_process';
import {
  CLI_CACHE_FILE,
  SKILL_CACHE_FILE,
  NPM_VIEW_VERSION_CMD,
  writeCache,
  touchCache,
} from './version-cache.js';
import { fetchLatestSkillVersion } from './skill-check.js';

// Hidden subcommand + env flags shared between the producer (`scheduleVersionRefresh`) and the
// consumer (the dispatch in index.ts), so the names can't drift between the two.
export const REFRESH_COMMAND = '__refresh-version-cache';
export const ENV_REFRESH_CLI = 'REKOR_REFRESH_CLI';
export const ENV_REFRESH_SKILL = 'REKOR_REFRESH_SKILL';

// Generous because nothing waits on us — this bounds an orphaned background process, not a user's
// command latency.
const REFRESH_TIMEOUT_MS = 10_000;

/**
 * Spawn the detached background refresher. It outlives this process, does the npm/GitHub lookups,
 * and writes the cache for the NEXT run — so the current command is never slowed by the network.
 * Touch the requested caches first, so concurrent foreground runs within the TTL don't each spawn a
 * refresher. `entryPath` is the CLI entry file to re-invoke (passed by index.ts).
 */
export function scheduleVersionRefresh(
  opts: { cli: boolean; skill: boolean },
  entryPath: string,
): void {
  try {
    if (opts.cli) touchCache(CLI_CACHE_FILE);
    if (opts.skill) touchCache(SKILL_CACHE_FILE);
    spawn(process.execPath, [entryPath, REFRESH_COMMAND], {
      detached: true,
      stdio: 'ignore',
      windowsHide: true, // no flashing console window on Windows (fires on every command past the TTL)
      env: {
        ...process.env,
        [ENV_REFRESH_CLI]: opts.cli ? '1' : '',
        [ENV_REFRESH_SKILL]: opts.skill ? '1' : '',
      },
    }).unref();
  } catch {
    // Never block the CLI on a failed background spawn.
  }
}

function refreshCliCache(): Promise<void> {
  return new Promise((resolve) => {
    exec(NPM_VIEW_VERSION_CMD, { timeout: REFRESH_TIMEOUT_MS }, (err, stdout) => {
      if (!err) {
        const latest = stdout.trim();
        if (latest) writeCache(CLI_CACHE_FILE, { lastCheck: Date.now(), latest });
      }
      resolve();
    });
  });
}

async function refreshSkillCache(): Promise<void> {
  const latest = await fetchLatestSkillVersion(REFRESH_TIMEOUT_MS);
  if (latest) writeCache(SKILL_CACHE_FILE, { lastCheck: Date.now(), latest });
}

/**
 * Entry point for the detached child (dispatched on `REFRESH_COMMAND` in index.ts). Refreshes only
 * the caches flagged by env, then returns. Never prints; never throws. Inert when neither flag is
 * set, so a manual `rekor __refresh-version-cache` does nothing.
 */
export async function runVersionRefresh(): Promise<void> {
  try {
    await Promise.all([
      process.env[ENV_REFRESH_CLI] ? refreshCliCache() : Promise.resolve(),
      process.env[ENV_REFRESH_SKILL] ? refreshSkillCache() : Promise.resolve(),
    ]);
  } catch {
    // A crashed background refresher stays silent.
  }
}
